<template>
  <div>
    <v-dialog
      v-model="isOpen"
      max-width="600px"
      :fullscreen="mdAndDown"
      persistent
    >
      <v-card data-testid="topics-topic-dialog">
        <v-card-title class="d-flex justify-space-between align-center bg-primary">
          <span class="text-white">
            <span>{{ dialogTitle }}</span>
          </span>
          <v-btn
            icon
            variant="text"
            color="white"
            @click="() => closeDialog(true)"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-form
          ref="form"
          @submit.prevent="saveTopic"
        >
          <v-card-text>
            <v-alert
              v-if="isNotCompliant"
              type="error"
              variant="tonal"
              density="compact"
              class="mb-4"
              role="alert"
              data-testid="topics-topic-not-compliant"
            >
              {{ t('topics.dialog.not-compliant') }}
            </v-alert>

            <v-text-field
              v-model="topicData.name"
              :label="t('topics.dialog.name-label')"
              :placeholder="t('topics.list.card.name-placeholder')"
              :rules="rules.name"
              :readonly="isReadOnly"
              required
              variant="filled"
              density="comfortable"
              class="mb-4"
              data-testid="topics-topic-name"
            />

            <v-textarea
              v-model="topicData.description"
              :label="t('topics.dialog.description-label')"
              :placeholder="t('topics.list.card.description-placeholder')"
              :rules="rules.description"
              :readonly="isReadOnly"
              variant="filled"
              density="comfortable"
              auto-grow
              rows="3"
              counter="2000"
              data-testid="topics-topic-description"
            />

            <v-row v-if="topic?.links.length">
              <v-col
                cols="5"
                md="4"
              >
                {{ t('topics.dialog.source') }}
              </v-col>
              <v-col
                cols="7"
                md="8"
                class="d-flex flex-column gap-1"
              >
                <a
                  v-for="source in topic?.links"
                  :key="source"
                  :href="source"
                  target="_blank"
                  class="w-full text-truncate"
                  >{{ source }}</a
                >
              </v-col>
            </v-row>
            <v-row v-if="topic?.createdAt">
              <v-col
                cols="5"
                md="4"
              >
                {{ t('topics.dialog.created-on') }}
              </v-col>
              <v-col
                cols="7"
                md="8"
              >
                {{ formatDate(topic.createdAt) }}
              </v-col>
            </v-row>

            <v-row v-if="topic?.updatedAt">
              <v-col
                cols="5"
                md="4"
              >
                {{ t('topics.dialog.last-edited-on') }}
              </v-col>
              <v-col
                cols="7"
                md="8"
              >
                {{ formatDate(topic.updatedAt) }}
              </v-col>
            </v-row>
          </v-card-text>

          <v-card-actions
            :class="{
              'pa-4': true,
              'flex-column': xs,
              'pb-16': xs,
            }"
          >
            <Permission permission="topic:write">
              <v-btn
                v-if="isEditing && props.topic && props.topic.isUserEditable && !isArchived"
                variant="text"
                data-testid="topics-open-archive-topic"
                @click="confirmArchive = true"
                :class="{ 'order-2 w-full': xs }"
              >
                <v-icon start>mdi-archive</v-icon>
                {{ t('topics.dialog.archive') }}
              </v-btn>
              <v-btn
                v-if="isArchived"
                variant="text"
                color="error"
                :loading="isArchiving"
                :class="{ 'w-full': xs }"
                data-testid="topics-unarchive-topic"
                @click="unarchiveTopic"
              >
                {{ t('topics.dialog.unarchive') }}
              </v-btn>
            </Permission>

            <v-spacer />
            <v-btn
              v-if="!isReadOnly"
              variant="flat"
              color="primary"
              type="submit"
              :class="{ 'px-4': true, 'w-full': xs }"
              :loading="isLoading"
              :data-testid="isEditing ? 'topics-update-topic' : 'topics-create-topic'"
            >
              <v-icon start>mdi-content-save</v-icon>
              {{ t('topics.dialog.save-and-close') }}
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>

    <!-- Archive Confirmation Dialog -->
    <v-dialog
      v-model="confirmArchive"
      max-width="400px"
    >
      <v-card>
        <v-card-title class="bg-error text-white">
          {{ t('topics.dialog.archive-confirm-title') }}
        </v-card-title>
        <v-card-text class="pt-4">
          {{ t('topics.dialog.archive-confirm-message') }}
        </v-card-text>
        <v-card-actions>
          <v-btn
            variant="text"
            @click="closeArchiveDialog"
          >
            {{ t('topics.dialog.cancel') }}
          </v-btn>
          <v-spacer />
          <v-btn
            color="error"
            variant="flat"
            :loading="isArchiving"
            data-testid="topics-archive-topic"
            @click="archiveTopic"
          >
            {{ t('topics.dialog.archive-confirm') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
// @ts-ignore - Ignore module resolution errors
import { useMutation } from '@vue/apollo-composable'
import { computed, inject, onMounted, ref, watch } from 'vue'
// @ts-ignore - Ignore module resolution errors
import { useI18n } from 'vue-i18n'
import { useDisplay } from 'vuetify'

import Permission from '@/components/Permission'
import type { TrackingPlugin } from '@/plugins/tracking'

import ARCHIVE_TOPIC from './graphql/archiveTopic.gql'
import CREATE_TOPIC from './graphql/createTopic.gql'
import UNARCHIVE_TOPIC from './graphql/unarchiveTopic.gql'
import UPDATE_TOPIC from './graphql/updateTopic.gql'
import TopicStatus from './TopicStatus'

const MIN_NAME_LENGTH = 5
const MAX_NAME_LENGTH = 100
const MAX_DESCRIPTION_LENGTH = 4000
const TOPIC_NOT_COMPLIANT = 'TOPIC_NOT_COMPLIANT'

interface Topic {
  id: string
  name: string
  description: string
  status: string
  isSystemGenerated: boolean
  isUserEditable: boolean
  links: string[]
  createdAt: string
  updatedAt: string
  landingpage?: {
    id: string
    slug: string
  } | null
}

interface TopicInput {
  name: string
  description: string
}

interface Props {
  modelValue: boolean
  topic?: Topic | null
  isEditing?: boolean
}

interface Emits {
  (e: 'update:modelValue', value: boolean): void
  (e: 'error', message: string): void
  (e: 'reload'): void
}

// Define props and emits
const props = withDefaults(defineProps<Props>(), {
  modelValue: false,
  topic: null,
  isEditing: false,
})

const emit = defineEmits<Emits>()

const tracking = inject<TrackingPlugin>('tracking')

// Refs
const form = ref<any>(null)
const isLoading = ref(false)
const isArchiving = ref(false)
const confirmArchive = ref(false)
const isNotCompliant = ref(false)
const topicData = ref<TopicInput>({
  name: '',
  description: '',
})

const isOpen = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value),
})

const isEditing = computed(
  () => props.topic?.isUserEditable && props.isEditing && props.topic !== null
)

const dialogTitle = computed(() => {
  if (isEditing.value) return t('topics.dialog.edit-title')
  if (!props.topic?.isUserEditable) return t('topics.dialog.view-topic')
  return t('topics.dialog.create-title')
})

const isArchived = computed(() => props.topic?.status === TopicStatus.ARCHIVED)

const isReadOnly = computed(
  () => (props.topic && (!props.topic?.isUserEditable || isArchived.value)) ?? undefined
)

const { t } = useI18n()
const { xs, mdAndDown } = useDisplay()

const { mutate: mutateCreateTopic, onError: onErrorCreateTopic } = useMutation(CREATE_TOPIC)
const { mutate: mutateUpdateTopic, onError: onErrorUpdateTopic } = useMutation(UPDATE_TOPIC)
const { mutate: mutateArchiveTopic, onError: onErrorArchiveTopic } = useMutation(ARCHIVE_TOPIC)
const { mutate: mutateUnarchiveTopic, onError: onErrorUnarchiveTopic } =
  useMutation(UNARCHIVE_TOPIC)

const handleGraphQLError = (error: any) => {
  const graphQLError = error.graphQLErrors?.[0]
  const code = graphQLError?.extensions?.code

  if (code === TOPIC_NOT_COMPLIANT) {
    isNotCompliant.value = true
  } else if (code === 'MAX_ACTIVE_TOPICS_REACHED') {
    emit('error', code)
  } else {
    emit('error', error.message || 'An error occurred')
  }
}

onErrorArchiveTopic(handleGraphQLError)
onErrorCreateTopic(handleGraphQLError)
onErrorUpdateTopic(handleGraphQLError)
onErrorUnarchiveTopic(handleGraphQLError)

const rules = {
  name: [
    (v: string) =>
      !!v ||
      t('topics.list.card.rules.required', {
        field: t('topics.list.card.rules.name-field'),
      }),
    (v: string) =>
      v.length >= MIN_NAME_LENGTH ||
      t('topics.list.card.rules.min', {
        field: t('topics.list.card.rules.name-field'),
        min: MIN_NAME_LENGTH,
      }),
    (v: string) =>
      v.length <= MAX_NAME_LENGTH ||
      t('topics.list.card.rules.max', {
        field: t('topics.list.card.rules.name-field'),
        max: MAX_NAME_LENGTH,
      }),
  ],
  description: [
    (v: string) =>
      !v ||
      v.length <= MAX_DESCRIPTION_LENGTH ||
      t('topics.list.card.rules.max', {
        field: t('topics.list.card.rules.description-field'),
        max: MAX_DESCRIPTION_LENGTH,
      }),
  ],
}

watch(
  () => props.topic,
  (newTopic: Topic | null) => {
    if (newTopic) {
      topicData.value = {
        name: newTopic.name || '',
        description: newTopic.description || '',
      }
    } else {
      resetForm()
    }
  }
)

watch(
  () => [topicData.value.name, topicData.value.description],
  () => {
    isNotCompliant.value = false
  }
)

const resetForm = () => {
  topicData.value = {
    name: '',
    description: '',
  }
  isNotCompliant.value = false
  if (form.value) {
    form.value.resetValidation()
  }
}

const closeDialog = (isUserAction = false) => {
  if (isUserAction && props.topic?.id) {
    const trackingMessage = props.topic.isSystemGenerated
      ? 'Topic Edit Cancel'
      : 'Own Topic Edit Cancel'
    tracking.event('Marketing Topics', 'Clicked', trackingMessage)
  }
  confirmArchive.value = false
  isOpen.value = false
  resetForm()
}

const closeArchiveDialog = () => {
  confirmArchive.value = false
}

const saveTopic = async () => {
  const { valid } = await form.value.validate()

  if (!valid) return

  isLoading.value = true
  isNotCompliant.value = false

  try {
    const trackingMessage = props.topic?.isSystemGenerated ? 'Topic Save' : 'Own Topic Save'
    if (isEditing.value && props.topic) {
      const isSaved = await updateTopic()
      if (!isSaved) return
      tracking.event('Marketing Topics', 'Clicked', trackingMessage)
    } else {
      const isSaved = await createTopic()
      if (!isSaved) return
      tracking.event('Marketing Topics', 'Clicked', trackingMessage)
      tracking.heapEvent('topic_created')
    }
    closeDialog()
  } finally {
    isLoading.value = false
  }
}

const createTopic = async () => {
  const result = await mutateCreateTopic({
    input: {
      name: topicData.value.name,
      description: topicData.value.description,
    },
  })
  if (!result) return false
  emit('reload')
  return true
}

const updateTopic = async () => {
  if (!props.topic) return false
  const result = await mutateUpdateTopic({
    input: {
      id: props.topic.id,
      name: topicData.value.name,
      description: topicData.value.description,
    },
  })
  if (!result) return false
  emit('reload')
  return true
}

const formatDate = (date: string) => {
  const dateObj = new Date(date)
  return new Intl.DateTimeFormat('de-DE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(dateObj)
}

const archiveTopic = async () => {
  if (!props.topic) return
  try {
    isArchiving.value = true

    await mutateArchiveTopic({
      id: props.topic.id,
    })
    const trackingMessage = props.topic.isSystemGenerated ? 'Topic Archive' : 'Own Topic Archive'
    tracking.event('Marketing Topics', 'Clicked', trackingMessage)
    closeDialog()
    emit('reload')
  } catch (error: any) {
    handleGraphQLError(error)
  } finally {
    isArchiving.value = false
  }
}

const unarchiveTopic = async () => {
  if (!props.topic) return
  try {
    isArchiving.value = true
    await mutateUnarchiveTopic({
      id: props.topic.id,
    })
    const trackingMessage = props.topic.isSystemGenerated
      ? 'Topic Reactivation'
      : 'Own Topic Reactivation'
    tracking.event('Marketing Topics', 'Clicked', trackingMessage)
    emit('reload')
  } catch (error: any) {
    handleGraphQLError(error)
  } finally {
    isArchiving.value = false
  }
}

onMounted(() => {
  if (props.topic) {
    topicData.value = {
      name: props.topic.name || '',
      description: props.topic.description || '',
    }
  } else {
    resetForm()
  }
})
</script>

<style scoped>
.v-card-title {
  word-break: break-word;
}
</style>
